import equal from "fast-deep-equal";
import { RotateCcw, Copy, Pencil, Check, Volume2, VolumeX } from "lucide-react";
import { memo, useState } from "react";
import type { ChatMessage } from "@/types/chat-message";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function PureMessageActions({
  message,
  isLoading,
  regenerate,
  role,
  onEdit,
  isReadonly,
  textToSpeechEnabled,
  isSpeaking,
  isTTSSupported,
  onSpeak,
  onStopSpeaking,
}: {
  message: ChatMessage;
  isLoading: boolean;
  role: string;
  regenerate?: () => void;
  onEdit?: () => void;
  isReadonly?: boolean;
  textToSpeechEnabled?: boolean;
  isSpeaking?: boolean;
  isTTSSupported?: boolean;
  onSpeak?: () => void;
  onStopSpeaking?: () => void;
}) {
  /**
   * State to track copy success feedback
   * Shows green checkmark for 2 seconds after successful copy
   */
  const [isCopied, setIsCopied] = useState(false);

  /**
   * Extracts text from an interactive tool-call part for clipboard copy.
   * Maps each interactive tool type to its human-readable text content.
   */
  const getToolPartText = (part: any): string => {
    const input = part.input;
    if (!input) return "";
    return "";
  };

  // Helper function to get message content for copying
  const getMessageContent = () => {
    if (!Array.isArray(message.parts) || message.parts.length === 0) {
      // Handle different message content types from ai SDK (legacy support)
      if (typeof (message as any).content === "string") {
        return (message as any).content;
      }
      if (Array.isArray((message as any).content)) {
        return (message as any).content
          .filter((part: any) => part.type === "text")
          .map((part: any) => part.text || "")
          .join("\n\n");
      }
      return "";
    }

    const segments: string[] = [];

    for (const part of message.parts) {
      const type = (part as any).type as string;
      if (type === "text") {
        const text = ((part as any).text || "").trim();
        if (text) segments.push(text);
      } else if (type?.startsWith("tool-")) {
        const text = getToolPartText(part).trim();
        if (text) segments.push(text);
      }
    }

    return segments.join("\n\n");
  };

  const handleCopy = async () => {
    const text = getMessageContent();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (HTTP)
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setIsCopied(true);
      toast.success("Message copied to clipboard");
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      toast.error("Failed to copy message");
    }
  };


  return (
    <TooltipProvider>
      <div className={
        cn(
          `flex items-center group-hover:opacity-100 transition-opacity`,
          role === 'user' && 'justify-end'
        )
      }>
        {/* Edit Button - only for user messages */}
        {message.role === 'user' && !isReadonly && onEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onEdit}
                className="hover:bg-muted"
                data-testid="message-edit-button"
              >
                <Pencil className="size-4 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit message</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Copy Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleCopy}
              className="hover:bg-muted"
            >
              {isCopied ? (
                <Check className="size-4 text-green-600" />
              ) : (
                <Copy className="size-4 text-muted-foreground" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isCopied ? "Copied!" : "Copy message"}</p>
          </TooltipContent>
        </Tooltip>

        {/* Text-to-Speech Button - only for assistant messages when enabled */}
        {message.role === "assistant" && textToSpeechEnabled && isTTSSupported && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => isSpeaking ? onStopSpeaking?.() : onSpeak?.()}
                className={cn(
                  "hover:bg-muted",
                  isSpeaking && "text-primary bg-primary/10"
                )}
              >
                {isSpeaking ? (
                  <VolumeX className="size-4 text-primary" />
                ) : (
                  <Volume2 className="size-4 text-muted-foreground" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isSpeaking ? "Stop speaking" : "Speak message"}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Regenerate Button - only for last assistant message */}
        {message.role === "assistant" && regenerate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={regenerate}
                className="hover:bg-muted"
                disabled={isLoading}
              >
                <RotateCcw className={cn("size-4 text-muted-foreground", isLoading && "animate-spin")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Regenerate response</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

export const MessageActions = memo(
  PureMessageActions,
  (prevProps, nextProps) => {
    if (prevProps.isLoading !== nextProps.isLoading) {
      return false;
    }
    if (prevProps.message.id !== nextProps.message.id) {
      return false;
    }
    if (prevProps.textToSpeechEnabled !== nextProps.textToSpeechEnabled) {
      return false;
    }
    if (prevProps.isSpeaking !== nextProps.isSpeaking) {
      return false;
    }
    return true;
  }
);

MessageActions.displayName = 'MessageActions';
